import { Component, OnInit, OnDestroy, computed, effect, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService, UserProfile } from '../../core/services/auth.service';
import { TenantThemeService, TenantThemeConfig } from '../../core/services/tenant-theme.service';
import { TranslationService, Language } from '../../core/services/translation.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentUser: UserProfile | null = null;
  tenantTheme: TenantThemeConfig | null = null;
  isMenuOpen = false;
  currentLang = 'es';
  availableLanguages: Language[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private tenantThemeService: TenantThemeService,
    private translationService: TranslationService,
    private router: Router
  ) {
    this.availableLanguages = this.translationService.availableLanguages;

    // Subscribe to user profile changes from MSAL
    effect(() => {
      this.currentUser = this.authService.userProfile();
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close menu when clicking outside
    if (this.isMenuOpen) {
      const target = event.target as HTMLElement;
      const userMenu = target.closest('.header__user');
      if (!userMenu) {
        this.isMenuOpen = false;
      }
    }
  }

  ngOnInit(): void {

    this.tenantThemeService
      .getCurrentTheme()
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.tenantTheme = theme;
      });

    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLang = lang;
      });
  }

  changeLanguage(langCode: string): void {
    this.translationService.setLanguage(langCode);
  }

  getCurrentLanguage(): Language {
    return this.availableLanguages.find(l => l.code === this.currentLang) || this.availableLanguages[0];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  logout(): void {
    this.authService.logout();
  }

  getUserInitials(): string {
    if (!this.currentUser) return '';
    const names = this.currentUser.name.split(' ');
    return names.map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  }

  goToProfile(event: Event): void {
    event.preventDefault();
    this.isMenuOpen = false;
    this.router.navigate(['/profile']);
  }

  goToSettings(event: Event): void {
    event.preventDefault();
    this.isMenuOpen = false;
    this.router.navigate(['/settings']);
  }

  goToHome(): void {
    this.router.navigate(['/home']);
  }
}
